import { useState } from 'react'
import { usePageMeta } from '../lib/usePageMeta.js'
import { SITE } from '../lib/site.js'
import { TRADE_OPTIONS } from '../lib/content.js'
import { Icon } from '../components/icons.jsx'
import { Button, PageHero, Section } from '../components/ui.jsx'

const NEXT = [
  { step: '1', title: 'We reply within one business day', body: 'A real person, by phone or email, to confirm a few details.' },
  { step: '2', title: 'You get the audit in 48 hours', body: 'Site, Google profile, ads and reviews, with what each gap is costing you.' },
  { step: '3', title: 'A plan with a price', body: 'A 90-day scope and forecast. Take it or leave it, no pressure.' },
]

const inputClass =
  'w-full rounded-[9px] border border-line bg-white px-[14px] py-[11px] text-[15px] text-ink placeholder:text-faint focus:border-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(242,107,29,0.2)]'
const labelClass = 'text-[13px] font-semibold text-ink'

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-y-[6px]">
      <span className={labelClass}>{label}</span>
      {children}
    </label>
  )
}

const EMPTY = { name: '', company: '', trade: '', phone: '', email: '', message: '' }

export default function Contact() {
  usePageMeta(
    'Contact',
    'Get a free 48-hour marketing audit for your contracting business. We reply within one business day.',
  )
  const [form, setForm] = useState(EMPTY)
  const [sent, setSent] = useState(false)

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  // No backend yet: open the visitor's mail client with everything prefilled.
  function onSubmit(e) {
    e.preventDefault()
    const subject = `Free audit request: ${form.company || form.name}`
    const body = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Trade: ${form.trade}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      '',
      form.message,
    ].join('\n')
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    setSent(true)
  }

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Get your free 48-hour audit."
        body="Tell us about your company. We will look at your site, Google profile, ads and reviews and send back what is leaking and what it is worth."
      />
      <Section id="contact-form">
        <div className="grid gap-y-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-x-14">
          <form
            onSubmit={onSubmit}
            className="flex flex-col gap-y-5 rounded-[20px] border border-line bg-white p-7 shadow-card sm:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your name">
                <input className={inputClass} required value={form.name} onChange={update('name')} placeholder="Mike Alvarez" autoComplete="name" />
              </Field>
              <Field label="Company">
                <input className={inputClass} required value={form.company} onChange={update('company')} placeholder="Alvarez Roofing" autoComplete="organization" />
              </Field>
            </div>
            <Field label="Trade">
              <select className={inputClass} required value={form.trade} onChange={update('trade')}>
                <option value="" disabled>
                  Pick your trade
                </option>
                {TRADE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Phone">
                <input className={inputClass} type="tel" required value={form.phone} onChange={update('phone')} placeholder="(555) 555-0123" autoComplete="tel" />
              </Field>
              <Field label="Email">
                <input className={inputClass} type="email" required value={form.email} onChange={update('email')} placeholder="you@company.com" autoComplete="email" />
              </Field>
            </div>
            <Field label="What is going on with your marketing?">
              <textarea
                className={`${inputClass} min-h-[120px] resize-y`}
                value={form.message}
                onChange={update('message')}
                placeholder="Where your leads come from today, what you have tried, and what a great month looks like."
              />
            </Field>
            <div className="flex flex-col gap-y-3 pt-1">
              <Button type="submit" size="lg" icon="arrowRight">
                Request my free audit
              </Button>
              {sent ? (
                <p className="rounded-[10px] bg-[#ecfdf5] px-4 py-3 text-[14px] font-semibold text-[#047857]">
                  Your email app should have opened with the request. If it did not, email us at{' '}
                  <a href={`mailto:${SITE.email}`} className="underline">
                    {SITE.email}
                  </a>
                  .
                </p>
              ) : (
                <p className="text-[13px] text-faint">
                  No spam and no pitch deck. We reply within one business day.
                </p>
              )}
            </div>
          </form>

          <aside className="flex flex-col gap-y-6">
            <div className="flex flex-col gap-y-5 rounded-[20px] bg-night p-7 text-white sm:p-9">
              <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#ffb27a]">
                Rather talk?
              </span>
              <a href={SITE.phoneHref} className="flex items-center gap-x-3 text-[22px] font-extrabold tracking-[-0.02em]">
                <Icon name="phone" size={22} className="text-primary" />
                {SITE.phone}
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-x-3 text-[16px] font-semibold text-[#dbe1ec]">
                <Icon name="mail" size={20} className="text-primary" />
                {SITE.email}
              </a>
              <div className="flex items-center gap-x-3 text-[14px] text-[#b6bfd0]">
                <Icon name="clock" size={18} className="text-primary" />
                {SITE.hours}
              </div>
            </div>

            <div className="flex flex-col gap-y-5 rounded-[20px] border border-line bg-surface p-7 sm:p-9">
              <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-faint">
                What happens next
              </span>
              <ol className="flex flex-col gap-y-5">
                {NEXT.map((n) => (
                  <li key={n.step} className="flex gap-x-4">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[13px] font-extrabold text-white">
                      {n.step}
                    </span>
                    <div className="flex flex-col gap-y-1">
                      <span className="text-[15px] font-bold text-ink">{n.title}</span>
                      <span className="text-[14px] leading-[1.55] text-dim">{n.body}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </Section>
    </>
  )
}
